import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../../api.js';
import MatchingRequests from '../requests/MatchingRequests.jsx';
import { useAuth } from '../../auth.jsx';
import { useI18n, usePageTitle } from '../../i18n/index.jsx';
import { formatDate } from '../../format.js';
import Icon from '../../components/Icon.jsx';
import UpgradeCard from '../../components/UpgradeCard.jsx';
import { AccountHead, useAccount, allowed } from './AccountLayout.jsx';
import TeamChanges from './TeamChanges.jsx';

// Tableau de bord: product counts by status, new quote requests, profile
// completeness with its checklist, and "+ Add a product".
export default function DashboardHome() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { summary: s } = useAccount();
  const { hash } = useLocation();
  usePageTitle('accountNav.dashboard');
  const [latest, setLatest] = useState(null);
  useEffect(() => { api('/account/requests').then((r) => setLatest(r.slice(0, 4))).catch(() => setLatest([])); }, []);
  useEffect(() => { if (hash === '#completeness' && s) document.getElementById('completeness')?.scrollIntoView({ block: 'start' }); }, [hash, s]);
  if (!s) return <p className="muted">{t('common.loading')}</p>;

  const p = s.products;
  const stats = [
    ['live', p.live, '/account/products?status=approved'],
    ['pending', p.pending, '/account/products?status=pending'],
    ['draft', p.draft, '/account/products?status=draft'],
    ['rejected', p.rejected, '/account/products?status=rejected'],
  ];
  const todo = s.completeness.items.filter((i) => !i.done);
  const canAdd = user.isSeller && user.emailVerified && user.profileComplete && allowed(user, 'products.create');
  const addBtn = canAdd
    ? <Link className="btn btn-gold" to="/account/listings/new"><Icon name="plus" size={18} /> {t('accountNav.add')}</Link>
    : null;

  return (
    <div className="acc-page">
      <AccountHead place="ws.dashboard" title={t('dashboardHome.hello', { name: user.name?.split(' ')[0] || user.companyName })} lead={t('dashboardHome.lead')} actions={addBtn} />
      {!user.emailVerified && <div className="callout callout-warning"><p>{t('account.verifyToPost')}</p></div>}
      {user.emailVerified && !user.profileComplete && <div className="callout callout-warning"><p>{t('account.completeToPost')} <Link to="/account/complete-profile">{t('account.completeProfileLink')}</Link></p></div>}
      {!user.isSeller && <UpgradeCard reason="sell" />}

      <div className="acc-stats">
        {stats.map(([k, n, to]) => (
          <Link key={k} to={to} className={`acc-stat acc-stat-${k}`}><strong>{n}</strong><span>{t(`dashboardHome.stats.${k}`)}</span></Link>
        ))}
        <Link to="/account/requests?status=new" className={`acc-stat${s.requests.new ? ' acc-stat-new' : ''}`}><strong>{s.requests.new}</strong><span>{t('dashboardHome.stats.requests')}</span></Link>
      </div>

      <div className="acc-dash-grid">
        <section className="card" id="completeness" aria-labelledby="dash-complete">
          <div className="acc-complete-head">
            <div className="acc-ring" style={{ '--p': s.completeness.percent }} role="img" aria-label={t('accountNav.completeness', { n: s.completeness.percent })}><span>{s.completeness.percent}%</span></div>
            <div>
              <h2 className="acc-card-title" id="dash-complete">{todo.length ? t('dashboardHome.completeTitle') : t('dashboardHome.completeDone')}</h2>
              <p className="muted small">{todo.length ? t('dashboardHome.completeLead', { n: todo.length }) : t('dashboardHome.completeDoneLead')}</p>
            </div>
          </div>
          <ul className="acc-check">
            {[...todo, ...s.completeness.items.filter((i) => i.done)].map((i) => (
              <li key={i.key} className={i.done ? 'is-done' : ''}>
                {i.done
                  ? <span><span className="acc-check-box" aria-hidden="true">✓</span><span>{t(`dashboardHome.check.${i.key}`)}</span></span>
                  : <Link to={`/account/${i.to}`}><span className="acc-check-box" aria-hidden="true" /><span>{t(`dashboardHome.check.${i.key}`)}</span><Icon name="arrow" size={16} /></Link>}
              </li>
            ))}
          </ul>
        </section>

        <div className="acc-page">
          <section className="card">
            <div className="acc-section-title"><h2 className="acc-card-title">{t('dashboardHome.latestRequests')}</h2><Link to="/account/requests" className="small">{t('dashboardHome.seeAll')} →</Link></div>
            {latest === null ? <p className="muted small">{t('common.loading')}</p> : latest.length === 0 ? <p className="muted small">{t('requestsPage.empty')}</p> : (
              <ul className="acc-mini-list">
                {latest.map((r) => (
                  <li key={r.id}><Link to={`/account/requests/${r.id}`}>
                    <span className={`chip-status chip-${r.status}`}>{t(`requestsPage.status.${r.status}`)}</span>
                    <span><strong>{r.listing.title}</strong><small>{r.buyer.company || r.buyer.name} · {formatDate(r.createdAt, lang)}{r.quantity ? ` · ${r.quantity}` : ''}</small></span>
                  </Link></li>
                ))}
              </ul>
            )}
          </section>
          <section className="card">
            <h2 className="acc-card-title">{t('dashboardHome.glance')}</h2>
            <dl className="acc-kv">
              <dt>{t('dashboardHome.totalProducts')}</dt><dd>{s.limit.max !== null ? t('productsPage.limit', { used: s.limit.used, max: s.limit.max }) : p.total}</dd>
              <dt>{t('dashboardHome.hiddenProducts')}</dt><dd>{p.hidden}</dd>
              <dt>{t('dashboardHome.views')}</dt><dd>{p.views}</dd>
              <dt>{t('dashboardHome.companyReview')}</dt><dd>{t(`dashboardHome.review.${s.company.review}`)}</dd>
              <dt>{t('dashboardHome.documents')}</dt><dd>{t('dashboardHome.documentsValue', { n: s.documents.total, pending: s.documents.pending })}</dd>
            </dl>
          </section>
        </div>
      </div>
      <MatchingRequests />
      <TeamChanges />
    </div>
  );
}
